import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Filter, Swords } from 'lucide-react';
import api from '../../lib/api';
import { RARITY_UI } from '../../lib/rarity';

export default function BattleHistory() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/battles/mine?limit=100').then(r => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? rows :
    filter === 'wins' ? rows.filter(b => b.winner === 'challenger') :
    filter === 'losses' ? rows.filter(b => b.winner !== 'challenger') :
    rows.filter(b => (b.mode || 'practice') === filter);

  const summary = {
    total: rows.length,
    wins: rows.filter(b => b.winner === 'challenger').length,
  };

  return (
    <div className="space-y-4" data-testid="battle-history-root">
      <button onClick={() => nav('/battle')} data-testid="battle-history-back" className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Back to Arena</button>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Battle</p>
        <h2 className="font-serif text-3xl">Match History</h2>
        <p className="text-xs text-muted-foreground mt-1">{summary.total} matches · {summary.wins} wins</p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        {['all', 'wins', 'losses', 'practice', 'ranked', 'casual'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`battle-history-filter-${f}`}
            className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${
              filter === f ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground'
            }`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl bg-card border border-white/5 p-8 text-center" data-testid="battle-history-empty">
          <Swords className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="font-serif text-lg mt-2">No matches</p>
          <p className="text-xs text-muted-foreground mt-1">Head to Practice or Ranked to start battling.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(b => {
            const won = b.winner === 'challenger';
            const chUI = RARITY_UI[b.challenger_snapshot?.rarity] || RARITY_UI.common;
            const opUI = RARITY_UI[b.opponent_snapshot?.rarity] || RARITY_UI.common;
            return (
              <div key={b.battle_id} className="rounded-2xl bg-card border border-white/5 p-3" data-testid="battle-history-row">
                <div className="flex items-center gap-2">
                  <div className={`w-1 h-10 rounded-full ${won ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="truncate" style={{ color: chUI.color }}>{b.challenger_snapshot?.name}</span>
                      <span className="text-muted-foreground">vs</span>
                      <span className="truncate" style={{ color: opUI.color }}>{b.opponent_snapshot?.name}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {(b.mode || 'practice').toUpperCase()} · Best of {b.best_of} · {new Date(b.created_at).toLocaleString()}
                      {b.arena && <> · Arena: {b.arena}</>}
                      {b.potion_used && <> · Potion: {b.potion_used}</>}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-[10px] uppercase tracking-widest ${won ? 'text-emerald-400' : 'text-red-400'}`}>{won ? 'Won' : 'Lost'}</p>
                    <p className="font-mono text-sm tabular-nums">{b.round_wins.challenger}–{b.round_wins.opponent}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
